import React, { useState, useEffect } from 'react'
import { C, ui, muteColor } from '../theme'

const h = ui.h
const inputStyle = { ...ui.input, marginBottom: 0 }
const btn = ui.btn

// Identity + membership panel (the "Campaign" world tab). Joining is code-gated: enter a
// join code (or arrive via a ?join= link) to bind this device to a faction/DM, or — for an
// unclaimed faction — claim it by choosing its code. Players are trusted; codes are a soft
// claim so people don't land in the wrong faction, not real auth.
export default function SidePanel({ user, members, factions, myMembership, onIdentify, onJoin, pendingJoinCode }) {
  const [name, setName] = useState('')
  const [mode, setMode] = useState('code')          // 'code' (have one) | 'claim' (set one)
  const [code, setCode] = useState('')
  const [claimTarget, setClaimTarget] = useState('') // faction id, or 'dm'
  const [setCodeVal, setSetCodeVal] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const hasDm = members.some(m => m.role === 'dm')
  const unclaimed = factions.filter(f => !f.claimed)

  // A ?join= link prefills the code and drops us into "enter a code" mode.
  useEffect(() => { if (pendingJoinCode) { setMode('code'); setCode(pendingJoinCode) } }, [pendingJoinCode])
  // Default the claim dropdown to the first sensible target.
  useEffect(() => {
    if (claimTarget) return
    if (!hasDm) setClaimTarget('dm')
    else if (unclaimed.length) setClaimTarget(String(unclaimed[0].id))
  }, [hasDm, unclaimed, claimTarget])

  const run = async (body) => {
    setError(null); setBusy(true)
    const res = await onJoin(body)
    setBusy(false)
    if (!res.ok) setError(res.data.error || `failed (${res.status})`)
    else { setCode(''); setSetCodeVal('') }
  }
  const submitCode = () => code.trim() && run({ code: code.trim() })
  const submitClaim = () => {
    if (!setCodeVal.trim()) { setError('choose a join code'); return }
    if (claimTarget === 'dm') run({ role: 'dm', setCode: setCodeVal.trim() })
    else run({ role: 'player', factionId: Number(claimTarget), setCode: setCodeVal.trim() })
  }

  const canClaim = !hasDm || unclaimed.length > 0
  const tab = (id, label) => (
    <button onClick={() => { setMode(id); setError(null) }} style={{
      flex: 1, padding: '5px 8px', fontSize: 12, cursor: 'pointer', borderRadius: 5,
      fontFamily: "'Cinzel', serif", letterSpacing: 0.3,
      border: `1px solid ${mode === id ? C.gold : C.rule}`,
      background: mode === id ? C.gold : 'transparent', color: mode === id ? C.paperRaised : C.inkSoft,
    }}>{label}</button>
  )

  return (
    <div>
      <div style={h}>You</div>
      {!user ? (
        <div>
          <input style={inputStyle} placeholder="Enter your name" value={name}
            onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && name.trim() && onIdentify(name.trim())} />
          <button style={{ ...btn, marginTop: 8 }} disabled={!name.trim()} onClick={() => onIdentify(name.trim())}>Continue</button>
          {pendingJoinCode && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>Enter your name to use your invite code.</div>}
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 600 }}>{user.displayName}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            {myMembership
              ? <>Role: <b>{myMembership.role === 'dm' ? 'DM' : 'Player'}</b>{myMembership.factionName ? ` · ${myMembership.factionName}` : ''}</>
              : <span style={{ opacity: 0.7 }}>Not yet joined this campaign</span>}
          </div>

          {(!myMembership || myMembership.role !== 'dm') && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {tab('code', 'Have a code')}
                {tab('claim', 'Claim a slot')}
              </div>

              {mode === 'code' ? (
                <>
                  {pendingJoinCode && <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Invite code from your link — click Join.</div>}
                  <input style={inputStyle} placeholder="Join code" value={code}
                    onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitCode()} />
                  <button style={{ ...btn, marginTop: 8 }} disabled={busy || !code.trim()} onClick={submitCode}>
                    {myMembership ? 'Switch with a code' : 'Join'}
                  </button>
                </>
              ) : !canClaim ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>Every faction is already claimed — ask for its join code, or have a DM assign you.</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Claim an open slot and pick its code (share it with your allies).</div>
                  <select style={inputStyle} value={claimTarget} onChange={e => setClaimTarget(e.target.value)}>
                    {!hasDm && <option value="dm">DM (full visibility)</option>}
                    {unclaimed.map(f => <option key={f.id} value={f.id}>Player · {f.name}</option>)}
                  </select>
                  <input style={{ ...inputStyle, marginTop: 8 }} placeholder="Choose a memorable code" value={setCodeVal}
                    onChange={e => setSetCodeVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitClaim()} />
                  <button style={{ ...btn, marginTop: 8 }} disabled={busy || !setCodeVal.trim()} onClick={submitClaim}>Claim</button>
                </>
              )}
              {error && <div style={{ color: C.crimson, fontSize: 12, marginTop: 6 }}>⚠ {error}</div>}
            </div>
          )}
        </div>
      )}

      <div style={h}>Members ({members.length})</div>
      {members.length === 0 && <div style={{ opacity: 0.5, fontSize: 13 }}>No one has joined yet.</div>}
      {members.map(m => (
        <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: m.factionColor ? muteColor(m.factionColor) : C.inkFaint, display: 'inline-block' }} />
          <span style={{ flex: 1 }}>{m.displayName}</span>
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            {m.role === 'dm' ? 'DM' : (m.factionName || 'player')}
          </span>
        </div>
      ))}
    </div>
  )
}
